//src/pages/learner/assessmentPage.js
import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  initiateAssessments,
  saveLearnerResponse,
  scoreLearnerResponse,
  getNextConversationalQuestion,
  getLearnerLeaderboard,
  updateLearnerEmail,
  checkEligibility,
  deleteGeneratedEmailData,
  finishAssessment
} from "../../api/learner/learnerApi";
import { fetchbackendQuestions } from "../../api/questions/questionsApi";
import "./assessmentPage.css";
import { MdCancel } from "react-icons/md";

const AssessmentPage = () => {
  const location = useLocation();
  const { projectId, fullName, email, projectCode } = location.state;

  const [learnerResponseId, setLearnerResponseId] = useState(null);
  const [questions, setQuestions] = useState({
    subjective: [],
    objective: [],
    conversational: [],
  });
  const [currentSection, setCurrentSection] = useState("subjective");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userResponses, setUserResponses] = useState([]);
  const [currentResponse, setCurrentResponse] = useState("");
  const [timer, setTimer] = useState(180);
  const [loadingNextQuestion, setLoadingNextQuestion] = useState(false); // Loading state for next question
  const [leaderboard, setLeaderboard] = useState([]);
  const [learnerStats, setLearnerStats] = useState(null);
  const [isLeaderboardVisible, setIsLeaderboardVisible] = useState(true);

  const [ObjectiveQuestionsMarks, setObjectiveQuestionsMarks] = useState(1);
  const [selectedOption, setSelectedOption] = useState(null);

  const isAssessmentInitiated = useRef(false);

  // Fetch leaderboard every 5 seconds
  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const testType = projectCode.endsWith("PR") ? "Pre" : "Post";
        const { data } = await getLearnerLeaderboard(
          projectId,
          email,
          testType
        );
        setLeaderboard(data.top14);
        setLearnerStats(data.learner);
      } catch (error) {
        console.error("Error fetching leaderboard:", error);
      }
    };

    fetchLeaderboard();
    const interval = setInterval(fetchLeaderboard, 5000);
    return () => clearInterval(interval);
  }, [projectId, email]);

  const toggleLeaderboardVisibility = () => {
    setIsLeaderboardVisible((prev) => !prev);
  };

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await fetchbackendQuestions(projectId);
        setObjectiveQuestionsMarks(
          response.data.questions.marks_per_question.objective
        );
        //console.log(response);
        if (response.data.status) {
          setQuestions(response.data.questions);
        }
      } catch (error) {
        console.error("Error fetching questions:", error);
      }
    };

    fetchQuestions();
  }, [projectId]);

  useEffect(() => {
    if (timer === 0) {
      handleAutoSubmit();
    }

    const interval = setInterval(() => {
      setTimer((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [timer]);

  const pickResumeSpot = (sections, responses, convTotal) => {
    const order = ["subjective", "objective", "conversational"];
    const answered = { subjective: 0, objective: 0, conversational: 0 };
  
    responses.forEach(r => (answered[r.question_type] += 1));
  
    for (const sect of order) {
      const total =
        sect === "conversational"
          ? convTotal                     
          : sections[sect]?.length || 0;
      if (answered[sect] < total) {
        return { section: sect, index: answered[sect] }; 
      }
    }
    return { section: order[0], index: 0 }; 
  };

  useEffect(() => {
    const startOrResume = async () => {
      if (isAssessmentInitiated.current) return;
      isAssessmentInitiated.current = true;
  
      try {
        const response = await initiateAssessments(fullName, email, projectId, projectCode);
  
        setLearnerResponseId(response.data.learnerResponseId);
        setQuestions(response.data.sections);
  
        if (response.data.responses && response.data.responses.length) {
          const convAnswered = response.data.responses
            .filter(r => r.question_type === "conversational")
            .map(r => ({ question_text: r.question_text }));

          if (convAnswered.length) {
            response.data.sections.conversational = [
              ...response.data.sections.conversational,
              ...convAnswered.slice(1)              
            ];
          }
          const { section, index } = pickResumeSpot(
            response.data.sections,
            response.data.responses,
            response.data.convTotal
          );
          setCurrentSection(section);
          setCurrentQuestionIndex(index);
          setTimer(section === "objective" ? 60 : 180);
          if (
            section === "conversational" &&
            index === response.data.sections.conversational.length
          ) {
            try {
              const nxt = await getNextConversationalQuestion(
                response.data.learnerResponseId
              );
              if (nxt.data.question?.trim()) {
                setQuestions(prev => ({
                  ...prev,
                  conversational: [
                    ...prev.conversational,
                    { question_text: nxt.data.question },
                  ],
                }));
              }
            } catch (err) {
              console.error("Error fetching follow-up conversational question:", err);
            }
          }
        } else {
          const first = Object.keys(response.data.sections)[0];
          setCurrentSection(first);
          setCurrentQuestionIndex(0);
          setTimer(first === "objective" ? 60 : 180);
        }
      } catch (err) {
        console.error("Error initiating/resuming:", err);
      }
    };
    startOrResume();
  }, [fullName, email, projectId]);

  const handleAutoSubmit = () => {
    const currentQuestion = getCurrentQuestion();
    if (timer > 0) return;
    if (!currentResponse.trim()) {
      setUserResponses((prev) => [...prev, { response: "", score: 0 }]);
    } else {
      handleResponseSubmit(currentResponse);
    }
    proceedToNextQuestion();
  };

  const handleResponseSubmit = async (response) => {
    const currentQuestion = getCurrentQuestion();

    if (currentSection === "objective") {
      if (!selectedOption) {
        alert("Please select an option before submitting!");
        return;
      }
      const isCorrect = currentQuestion.answer === response;
      const score = isCorrect ? ObjectiveQuestionsMarks || 1 : 0;

      setUserResponses((prev) => [...prev, { response, score }]);

      try {
        await saveLearnerResponse(learnerResponseId, {
          question_id: currentQuestion._id,
          question_type: "objective",
          question_text: currentQuestion.question_text,
          response,
          score,
          start_time: new Date(),
          end_time: new Date(),
        });
      } catch (error) {
        console.error("Error saving objective response:", error);
      }
    } else {
      await scoreResponseWithAI(currentQuestion, response);
    }

    setCurrentResponse("");
    proceedToNextQuestion();
    setSelectedOption("");
  };

  const scoreResponseWithAI = async (question, response) => {
    try {
      setLoadingNextQuestion(true);

      const { data } = await scoreLearnerResponse(learnerResponseId, {
        questionId: question._id,
        questionType: currentSection,
        questionText: question.question_text,
        response,
      });

      setUserResponses((prev) => [...prev, { response, score: data.score }]);

      if (currentSection === "conversational") {
        const nextQuestionResponse = await getNextConversationalQuestion(
          learnerResponseId
        );

        if (nextQuestionResponse.data.question?.trim()) {
          // Append the next question dynamically
          setQuestions((prev) => ({
            ...prev,
            conversational: [
              ...prev.conversational,
              { question_text: nextQuestionResponse.data.question },
            ],
          }));
          setCurrentQuestionIndex((prev) => prev + 1);
          setTimer(180);
        } else {
          moveToNextSection();
        }
      }
    } catch (error) {
      console.error("Error scoring response:", error.message);
      alert("An error occurred. Please try again.");
    } finally {
      setLoadingNextQuestion(false);
    }
  };

  const proceedToNextQuestion = () => {
    const currentSectionQuestions = getCurrentSectionQuestions();
    const isLastQuestion =
      currentQuestionIndex === currentSectionQuestions.length - 1;

    if (isLastQuestion) {
      if (currentSection === "conversational") {
        // Wait for the backend to dynamically add the next question
        return;
      }
      moveToNextSection();
    } else {
      setCurrentQuestionIndex((prev) => prev + 1);
      setTimer(currentSection === "objective" ? 60 : 180);
    }
  };

  const moveToNextSection = () => {
    const nextSection = getNextSection();

    if (nextSection) {
      setCurrentSection(nextSection);
      setCurrentQuestionIndex(0);
      setTimer(nextSection === "objective" ? 60 : 180);
    } else {
      completeAssessment();
    }
  };

  // Handling no copy paste
  const handlePaste = (e) => {
    e.preventDefault();
  };

  const handleCopy = (e) => {
    e.preventDefault();
  };

  const handleCut = (e) => {
    e.preventDefault();
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
  };

  const handleCtrlKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && ['c', 'v', 'x'].includes(e.key)) {
      e.preventDefault();
    }
  };

  // const completeAssessment = () => {
  //   const totalScore = userResponses.reduce((acc, { score }) => acc + score, 0);
  //   //console.log('Assessment Complete. Total Score:', totalScore);
  //   document.body.innerHTML = `
  //     <div style="text-align: center; margin-top: 20%;">
  //       <h1>Wohoo! Assessment Complete</h1>
  //       <p>
  //         To view your results, please <a href="/login_register" style="color: blue; text-decoration: underline;">login/register</a> yourself.
  //       </p>
  //       <p><strong>OR</strong></p>
  //       <p>
  //         Click here to return to the <a href="/" style="color: blue; text-decoration: underline;">main page</a>.
  //       </p>
  //     </div>
  //   `;
  // };

  const completeAssessment = async () => {
    const totalScore = userResponses.reduce((acc, { score }) => acc + score, 0);
    const isEmailGenerated =
      email.startsWith("user_") && email.endsWith("@myassessment.ai");

    if (isEmailGenerated) {
      // Render a modal-like structure for email input
      document.body.innerHTML = `
        <div style="text-align: center; margin-top: 20%;">
          <h1>Wohoo! Assessment Completed!</h1>
          <p>To save and view your assessment result and to get your certificate, please enter a valid email ID:</p>
          <input id="userEmailInput" type="email" placeholder="Enter your email" style="padding: 10px; width: 80%; margin-top: 10px;"/>
          <br/>
          <button id="saveEmailButton" style="padding: 10px 20px; margin-top: 20px; cursor: pointer;">Save</button>
          <p><strong>OR</strong></p>
          <p>
            Click here to return to the <a href="/" style="color: blue; text-decoration: underline;">MyAssessment.AI</a>.
          </p>
        </div>
      `;

      // Add event listener to handle email save
      document
        .getElementById("saveEmailButton")
        .addEventListener("click", async () => {
          const userEmail = document
            .getElementById("userEmailInput")
            .value.trim();

          if (userEmail && validateEmail(userEmail)) {
            try {
              const data = await checkEligibility(
                projectId,
                userEmail,
                projectCode
              );
              //console.log(data);
              if (data.data.isEligible) {
                // Call the API to update the email
                await updateLearnerEmail(
                  learnerResponseId,
                  userEmail,
                  projectId,
                  projectCode
                );
                alert(
                  `To view your results, please use the email ${userEmail} to login/register.`
                );
                await finishAssessment(learnerResponseId);
                window.location.href = "/login_register";
              } else {
                //console.log(learnerResponseId);
                await deleteGeneratedEmailData(learnerResponseId);
                alert(
                  `You have already taken this assessment ${data.data.attemptCount}/${data.data.maxAttempts} times. Therefore, this existing assessment you have taken will not count towards your scores.`
                );
                window.location.href = "/";
              }
            } catch (error) {
              console.error("Error updating email:", error);
              alert("Failed to save your email. Please try again.");
            }
          } else {
            alert("Please enter a valid email address.");
          }
        });
    } else {
      // Render the default completion message for users with provided email
      document.body.innerHTML = `
        <div style="text-align: center; margin-top: 20%;">
          <h1>Wohoo! Assessment Completed!</h1>
          <p>
            To view your results, please <a href="/login_register" style="color: blue; text-decoration: underline;">login/register</a> with the email address you provided.
          </p>
          <p><strong>OR</strong></p>
          <p>
            Click here to return to the <a href="/" style="color: blue; text-decoration: underline;">main page</a>.
          </p>
        </div>
      `;
      await finishAssessment(learnerResponseId);
    }
  };

  const validateEmail = (email) => {
    // Regular expression to validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const getNextSection = () => {
    const availableSections = Object.keys(questions);
    const currentIndex = availableSections.indexOf(currentSection);
    return availableSections[currentIndex + 1] || null;
  };

  const getCurrentSectionQuestions = () => questions[currentSection] || [];
  const getCurrentQuestion = () =>
    getCurrentSectionQuestions()[currentQuestionIndex];

 return (
  <div className="min-h-screen bg-gradient-to-br from-white to-[#eef1ff] py-6">
    <div className="md:w-10/12 w-full mx-auto flex lg:flex-row flex-col gap-8 px-2">

      
      <div className="lg:w-8/12 w-full">
        
        
        <div className="flex justify-between items-center mb-4 px-2">
          <h3 className="md:text-3xl text-xl font-bold text-[#36369f]">
            Assessment
          </h3>

          <button
            className="lg:hidden bg-[#36369f] text-white px-3 py-1 rounded-lg text-sm"
            onClick={toggleLeaderboardVisibility}
          >
            {isLeaderboardVisible ? "View Leaderboard" : "Close"}
          </button>
        </div>

        
        <div className="bg-white rounded-2xl shadow-xl border border-[#e4e8ff] p-4 md:p-8">

          
          <p className="text-center font-semibold text-[#36369f] mb-4">
            Next Question In : <strong>{timer}</strong> s
          </p>

          {loadingNextQuestion ? (
            <p className="text-center my-5 text-gray-500">
              Loading next question...
            </p>
          ) : (
            getCurrentSectionQuestions().length > 0 && (
              <div>

                
                <h4 className="font-semibold text-[#36369f] mb-2">
                  Question
                </h4>
                <p className="text-base md:text-lg mb-6 text-gray-800">
                  {getCurrentQuestion()?.question_text}
                </p>

                
                <h4 className="font-semibold text-[#36369f] mb-3">
                  Answer
                </h4>

                {currentSection === "objective" ? (
                  <>
                    <div className="grid md:grid-cols-2 gap-4">
                      {getCurrentQuestion()?.options?.map((option, index) => (
                        <div
                          key={index}
                          className={`flex items-center p-3 border rounded-xl cursor-pointer transition-all
                          ${
                            selectedOption === option
                              ? "border-[#36369f] bg-[#f0f2ff]"
                              : "border-gray-200 hover:border-[#36369f]"
                          }`}
                          onClick={() => setSelectedOption(option)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedOption === option}
                            readOnly
                            className="mr-3"
                          />
                          <span className="font-semibold mr-2">
                            {String.fromCharCode(65 + index)}.
                          </span>
                          <span>{option}</span>
                        </div>
                      ))}
                    </div>

                    <button
                      className="mt-6 mx-auto block bg-[#36369f] text-white px-10 py-2 rounded-xl hover:bg-[#2d2db3] transition"
                      onClick={() => handleResponseSubmit(selectedOption)}
                    >
                      Submit Answer
                    </button>
                  </>
                ) : (
                  <>
                    <textarea
                      className="w-full border rounded-xl p-4 focus:outline-none focus:ring-2 focus:ring-[#36369f]"
                      rows="4"
                      value={currentResponse}
                      onChange={(e) => setCurrentResponse(e.target.value)}
                      onCopy={handleCopy}
                      onCut={handleCut}
                      onPaste={handlePaste}
                      onContextMenu={handleContextMenu}
                      onKeyDown={handleCtrlKeyDown}
                      placeholder="Enter your response..."
                    />

                    <button
                      className="mt-6 w-full bg-[#36369f] text-white py-2 rounded-xl hover:bg-[#2d2db3] transition disabled:bg-gray-400"
                      onClick={() => handleResponseSubmit(currentResponse)}
                      disabled={!currentResponse.trim()}
                    >
                      Submit Answer
                    </button>
                  </>
                )}
              </div>
            )
          )}
        </div>
      </div>

      
      <div className="lg:w-4/12 w-full">

        <h3 className="md:text-2xl text-xl font-bold text-[#36369f] text-center mb-4 hidden lg:block">
          Leaderboard
        </h3>

        
        <div className="hidden lg:block bg-white rounded-2xl shadow-xl border border-[#e4e8ff] p-4">
          <div className="h-[420px] overflow-y-auto">
            <table className="w-full text-center">
              <thead className="bg-[#36369f] text-white sticky top-0">
                <tr>
                  <th className="py-3">Rank</th>
                  <th>Name</th>
                  <th>Score</th>
                </tr>
              </thead>

              <tbody>
                {leaderboard.map((entry) => (
                  <tr
                    key={entry.rank}
                    className="border-b hover:bg-[#f5f6ff] transition"
                  >
                    <td className="py-2 font-bold">{entry.rank}</td>
                    <td>{entry.learner}</td>
                    <td>{entry.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        
        {!isLeaderboardVisible && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 lg:hidden">
            <div className="bg-white rounded-2xl w-11/12 max-w-md p-4">

              <div className="flex justify-between items-center mb-3">
                <h4 className="font-bold text-[#36369f]">Leaderboard</h4>
                <MdCancel
                  className="text-red-500 text-2xl cursor-pointer"
                  onClick={toggleLeaderboardVisibility}
                />
              </div>

              <div className="h-[400px] overflow-y-auto">
                <table className="w-full text-center">
                  <thead className="bg-[#36369f] text-white sticky top-0">
                    <tr>
                      <th className="py-2">Rank</th>
                      <th>Name</th>
                      <th>Score</th>
                    </tr>
                  </thead>

                  <tbody>
                    {leaderboard.map((entry) => (
                      <tr key={entry.rank} className="border-b">
                        <td className="py-2 font-bold">{entry.rank}</td>
                        <td>{entry.learner}</td>
                        <td>{entry.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);
};

export default AssessmentPage;